import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Flex,
  Text,
  SimpleGrid
} from '@chakra-ui/react';
import {
  faArrowDownToBracket,
  faMinus,
  faPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, OutlineCard } from '@morressier/alembic';
import {
  IEnrichedSubmissionFileSet,
  IFiguresAndTablesFile
} from '@publishing-engine/domain/models';
import Divider from '../Divider';
import dateFormat from 'dateformat';
import { downloadFile } from 'src/utils/file';
import FiguresDisplay from './GenericFieldComponents/Figures/FiguresDisplay';
import FileViewerButtonText from './FileViewerButtonText';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

interface Props {
  fileset: IEnrichedSubmissionFileSet;
  totalFileCount: number;
  submissionContextId: string;
}

const FileCard = ({
  title,
  file,
  submissionContextId
}: {
  title: string;
  file: any;
  submissionContextId: string;
}) => {
  if (!file) return null;

  return (
    <OutlineCard>
      <Flex flexDirection="column" padding={2}>
        <Text textStyle="headingLine">{title}</Text>

        <Flex justifyContent="space-between" alignItems="center">
          <Flex gap={2} alignItems="center" color="blue">
            <FileViewerButtonText
              file={file}
              submissionContextId={submissionContextId}
              textStyle="textBody"
              wordBreak={'break-word'}
              whiteSpace="normal"
              textAlign={'start'}
            >
              {file.name}
            </FileViewerButtonText>
          </Flex>
          <ButtonIcon
            icon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
            ariaLabel="download-file"
            onClick={() => downloadFile(file?.url)}
          />
        </Flex>

        <Text textStyle="textContext" color="gray.500">
          Added by {file?.userDetails?.firstName} {file?.userDetails?.lastName}{' '}
          ({file?.userDetails?.roleName}) on{' '}
          {dateFormat(file.eventDate, 'mmm d, yyyy')}
        </Text>
      </Flex>
    </OutlineCard>
  );
};

/* ------------------ main ------------------ */
const LABEL_MAP: Record<string, string> = {
  ARRIVEFiles: 'ARRIVE Checklist',
  AdditionalSupplementary: 'Supplementary File(s) (NOT for review)',
  CONSORTFiles: 'CONSORT Checklist',
  CoverLetter: 'Cover Letter',
  Figures: 'Figures',
  Tables: 'Tables',
  Manuscript: 'Manuscript',
  TitlePage: 'Title Page',
  Supplementary: 'Supplementary File(s) (for review)',
  StandardReportingGuidelinesFileDetails: 'Standard Reporting Guidelines',
  GraphicalAbstractImage: 'Graphical Abstract'
};

export const SubmissionFilesAccordion = ({
  fileset,
  totalFileCount,
  submissionContextId
}: Props) => {
  const { submissionContext } = useSubmissionData();
  const hasCOCEnabled = submissionContext?.config?.isCOCRecommendationRequired;

  return (
    <Accordion allowToggle defaultIndex={0}>
      <AccordionItem
        border={hasCOCEnabled ? 'none' : '1px solid'}
        borderColor="neutralPrimaryBorder"
        borderRadius={hasCOCEnabled ? undefined : '10px'}
        overflow={hasCOCEnabled ? undefined : 'hidden'}
      >
        {({ isExpanded }) => (
          <>
            {/* ---------- HEADER ---------- */}
            <AccordionButton
              padding={1}
              bgColor="surfacePrimaryFill"
              border={hasCOCEnabled ? '1px solid' : 'none'}
              borderColor="neutralPrimaryBorder"
            >
              <Flex
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Text
                  textStyle="headingLine"
                  ml={!hasCOCEnabled ? 2 : undefined}
                >
                  Submission Files ({totalFileCount})
                </Text>

                <Flex gap={3} alignItems="center">
                  <Box ml={2} p={2}>
                    <FontAwesomeIcon
                      icon={isExpanded ? faMinus : faPlus}
                      color="blue"
                    />
                  </Box>
                </Flex>
              </Flex>
            </AccordionButton>

            {/* ---------- CONTENT ---------- */}
            <AccordionPanel
              px={4}
              py={3}
              border={hasCOCEnabled ? '1px solid' : 'none'}
              borderTop={hasCOCEnabled ? 'none' : '1px solid'}
              borderColor="neutralPrimaryBorder"
              bgColor="surfaceSecondaryFill"
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {Object.entries(fileset).map(([key, value]) => {
                  return (
                    !Array.isArray(value) &&
                    Object.entries(value).length !== 0 && (
                      <FileCard
                        title={LABEL_MAP[key] || key}
                        file={value}
                        submissionContextId={submissionContextId}
                      />
                    )
                  );
                })}
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 1 }} spacing={6}>
                {Object.entries(fileset).map(([key, value]) => {
                  const fileCount = Array.isArray(value) ? value.length : 0;

                  return (
                    Array.isArray(value) &&
                    value.length > 0 && (
                      <OutlineCard mt={4} padding={3}>
                        <Text textStyle="headingLine" mb={2}>
                          {LABEL_MAP[key]} ({fileCount})
                        </Text>
                        {key === 'Figures' || key === 'Tables' ? (
                          <FiguresDisplay
                            figures={value as IFiguresAndTablesFile}
                            display={key === 'Figures' ? 'Figure' : 'Table'}
                          />
                        ) : (
                          value.map((file: any, i) => (
                            <>
                              <Flex justifyContent="space-between">
                                <Flex
                                  flexDirection="column"
                                  gap={1}
                                  align={'start'}
                                >
                                  <FileViewerButtonText
                                    file={file}
                                    submissionContextId={submissionContextId}
                                    textStyle="textBody"
                                    wordBreak={'break-word'}
                                    whiteSpace="normal"
                                    textAlign={'start'}
                                  >
                                    {file?.name || file?.file?.name}
                                  </FileViewerButtonText>
                                  <Text
                                    textStyle="textContext"
                                    color="gray.500"
                                  >
                                    Added by {file?.userDetails?.firstName}{' '}
                                    {file?.userDetails?.lastName} (
                                    {file?.userDetails?.roleName}) on{' '}
                                    {dateFormat(file.eventDate, 'mmm d, yyyy')}
                                  </Text>
                                  {file?.caption && (
                                    <Flex flexDirection="column">
                                      <Text textStyle="headingLine">
                                        Caption
                                      </Text>
                                      <Text textStyle="textBody">
                                        {file?.caption}
                                      </Text>
                                    </Flex>
                                  )}
                                </Flex>
                                <ButtonIcon
                                  icon={
                                    <FontAwesomeIcon
                                      icon={faArrowDownToBracket}
                                    />
                                  }
                                  onClick={() =>
                                    downloadFile(file?.url || file?.file?.url)
                                  }
                                  ariaLabel="download-file"
                                />
                              </Flex>
                              {value.length - 1 !== i && (
                                <Box py={2}>{Divider}</Box>
                              )}
                            </>
                          ))
                        )}
                      </OutlineCard>
                    )
                  );
                })}
              </SimpleGrid>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};
